import type { Nullable } from "@shared-kernel/core/types/nullable";

export enum FUNDER {
  AFDAS_INTERMITTENT_DU_SPECTACLE = "afdas_intermittent_du_spectacle",
  AFDAS_ARTISTE_AUTEUR = "afdas_artiste_auteur",
  AFDAS_ENTREPRISE = "afdas_entreprise",
  FRANCE_TRAVAIL = "france_travail",
  FIFPL = "fifpl",
  ATLAS = "atlas",
  AKTO = "akto",
  AGEFICE = "agefice",
  AUTRE = "other",
}

export const FUNDER_LABELS_LONG: Record<FUNDER, string> = {
  [FUNDER.AFDAS_INTERMITTENT_DU_SPECTACLE]: "AFDAS - Intermittent du spectacle (IS)",
  [FUNDER.AFDAS_ARTISTE_AUTEUR]: "AFDAS - Artiste auteur (AA)",
  [FUNDER.AFDAS_ENTREPRISE]: "AFDAS - Entreprise (Opéra de Paris, Radio France, autre)",
  [FUNDER.ATLAS]: "ATLAS",
  [FUNDER.FIFPL]: "FIFPL",
  [FUNDER.FRANCE_TRAVAIL]: "FRANCE TRAVAIL",
  [FUNDER.AGEFICE]: "AGEFICE",
  [FUNDER.AKTO]: "AKTO",
  [FUNDER.AUTRE]: "Autre",
};

export const FUNDER_LABELS_MEDIUM: Record<FUNDER, string> = {
  [FUNDER.AFDAS_INTERMITTENT_DU_SPECTACLE]: "AFDAS - Intermittent du spectacle",
  [FUNDER.AFDAS_ARTISTE_AUTEUR]: "AFDAS - Artiste auteur",
  [FUNDER.AFDAS_ENTREPRISE]: "AFDAS - Entreprise",
  [FUNDER.ATLAS]: "ATLAS",
  [FUNDER.FIFPL]: "FIFPL",
  [FUNDER.FRANCE_TRAVAIL]: "FRANCE TRAVAIL",
  [FUNDER.AGEFICE]: "AGEFICE",
  [FUNDER.AKTO]: "AKTO",
  [FUNDER.AUTRE]: "Autre",
};

export const FUNDER_LABELS_SHORT: Record<FUNDER, string> = {
  [FUNDER.AFDAS_INTERMITTENT_DU_SPECTACLE]: "AFDAS (IS)",
  [FUNDER.AFDAS_ARTISTE_AUTEUR]: "AFDAS (AA)",
  [FUNDER.AFDAS_ENTREPRISE]: "AFDAS (Entreprise)",
  [FUNDER.ATLAS]: "ATLAS",
  [FUNDER.FIFPL]: "FIFPL",
  [FUNDER.FRANCE_TRAVAIL]: "FRANCE TRAVAIL",
  [FUNDER.AGEFICE]: "AGEFICE",
  [FUNDER.AKTO]: "AKTO",
  [FUNDER.AUTRE]: "Autre",
};

// Helper to check if a funder is any AFDAS type
export const isAfdasFunder = (funder: FUNDER): boolean => {
  return [FUNDER.AFDAS_INTERMITTENT_DU_SPECTACLE, FUNDER.AFDAS_ARTISTE_AUTEUR, FUNDER.AFDAS_ENTREPRISE].includes(funder);
};

export const isAfdasISFunder = (funder: FUNDER): boolean => {
  return funder === FUNDER.AFDAS_INTERMITTENT_DU_SPECTACLE;
};

interface AFDAS_INTERMITTENT_DU_SPECTACLE {
  type: FUNDER.AFDAS_INTERMITTENT_DU_SPECTACLE;
  payload: {
    id: string;
    // Becomes null after a while
    password: Nullable<string>;
  };
}

interface FRANCE_TRAVAIL {
  type: FUNDER.FRANCE_TRAVAIL;
  payload: {
    id: string;
    postCode: string;
  };
}

interface AUTRE {
  type: FUNDER.AUTRE;
  payload: {
    customName: string;
  };
}

interface AFDAS_ENTREPRISE {
  type: FUNDER.AFDAS_ENTREPRISE;
  payload: {
    culturalCompanyName: string;
    desiredQuoteAmount: number;
  };
}

interface FUNDER_WITHOUT_PAYLOAD {
  type: FUNDER.AFDAS_ARTISTE_AUTEUR | FUNDER.AGEFICE | FUNDER.AKTO | FUNDER.ATLAS | FUNDER.FIFPL;
}

export type FUNDER_TYPE = AFDAS_INTERMITTENT_DU_SPECTACLE | FRANCE_TRAVAIL | AUTRE | AFDAS_ENTREPRISE | FUNDER_WITHOUT_PAYLOAD;

// Helper function to get display name for a funder
// Returns custom name for AUTRE funder, otherwise returns standard label
export const getFunderDisplayName = (funder: FUNDER_TYPE, useShortLabel = false): string => {
  const labels = useShortLabel ? FUNDER_LABELS_SHORT : FUNDER_LABELS_MEDIUM;

  if (funder.type === FUNDER.AUTRE && "customName" in funder.payload) {
    return funder.payload.customName;
  }

  return labels[funder.type];
};
